import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout } from "@/components/marketing/Layout";
import { ArrowLeft, ArrowUpRight, Clock, Calendar } from "lucide-react";
import {
  getArticle,
  getRelatedArticles,
  SUPPORT_ARTICLES,
  type ArticleSection,
  type SupportArticle,
} from "@/lib/support-articles";
import {
  useAutoTranslate,
  collectBlockStrings,
  applyBlockStrings,
} from "@/components/AutoTranslate";

export const Route = createFileRoute("/support/$slug")({
  loader: ({ params }) => {
    const article = getArticle(params.slug);
    if (!article) throw notFound();
    return { article, related: getRelatedArticles(params.slug) };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found — Reality Twin Support" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { article } = loaderData;
    const seo = buildSeo({
      path: `/support/${params.slug}`,
      title: `${article.title} — Reality Twin Support`,
      description: article.summary,
      type: "article",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: article.title,
            description: article.summary,
            url: `https://realitytwin.io/support/${params.slug}`,
            datePublished: article.updated,
            dateModified: article.updated,
            inLanguage: "en",
            author: { "@type": "Organization", name: "Reality Twin" },
            publisher: { "@type": "Organization", name: "Ruznic Marketing" },
            about: article.category,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://realitytwin.io/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Support",
                item: "https://realitytwin.io/support",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://realitytwin.io/support/${params.slug}`,
              },
            ],
          }),
        },
      ],
    };
  },
  component: ArticlePage,
  errorComponent: ArticleError,
  notFoundComponent: ArticleNotFound,
});

function renderSection(s: ArticleSection, i: number) {
  switch (s.type) {
    case "h2":
      return (
        <h2
          key={i}
          className="mt-12 mb-4 font-serif text-[28px] leading-tight tracking-[-0.02em] text-foreground"
        >
          {s.text}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="mt-8 mb-2 text-[17px] font-medium text-foreground">
          {s.text}
        </h3>
      );
    case "p":
      return (
        <p key={i} className="mb-5 text-[15.5px] leading-[1.7] text-muted-foreground">
          {s.text}
        </p>
      );
    case "ul":
      return (
        <ul
          key={i}
          className="mb-5 list-disc pl-6 space-y-2 text-[15.5px] leading-[1.7] text-muted-foreground"
        >
          {s.items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol
          key={i}
          className="mb-5 list-decimal pl-6 space-y-2 text-[15.5px] leading-[1.7] text-muted-foreground"
        >
          {s.items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre
          key={i}
          className="mb-6 overflow-x-auto rounded-lg border border-border bg-cream p-4 text-[13px] leading-[1.6] font-mono"
        >
          <code>{s.text}</code>
        </pre>
      );
    case "callout":
      return (
        <div
          key={i}
          className={`mb-6 rounded-lg border-l-2 p-4 text-[14.5px] leading-[1.65] ${
            s.kind === "warn"
              ? "border-foreground/60 bg-cream text-foreground"
              : "border-foreground/30 bg-cream text-muted-foreground"
          }`}
        >
          {s.text}
        </div>
      );
  }
}

function ArticlePage() {
  const { article, related } = Route.useLoaderData();

  // Long-form article content is authored in English; translate the headings,
  // body blocks (code left intact) and related-article labels into the reader's
  // language via the cached translator.
  const CHROME = [
    "Support Center", // 0
    "Updated", // 1
    "min read", // 2
    "Related in", // 3
    "Did this article solve your problem?", // 4
    "If not,", // 5
    "email us", // 6
    "— a human on the founding team replies, usually within a business day.", // 7
  ];
  const relStrings = related.flatMap((r: SupportArticle) => [r.title, r.summary]);
  const bodyStrings = collectBlockStrings(article.body as any);
  const src = [
    ...CHROME,
    article.title,
    article.summary,
    article.category,
    ...relStrings,
    ...bodyStrings,
  ];
  const { out } = useAutoTranslate(src);
  let p = 0;
  const C = CHROME.map(() => out[p++]);
  const titleT = out[p++] ?? article.title;
  const summaryT = out[p++] ?? article.summary;
  const categoryT = out[p++] ?? article.category;
  const relT = related.map(() => ({ title: out[p++], summary: out[p++] }));
  const bodyT = applyBlockStrings(article.body as any, out.slice(p)) as ArticleSection[];

  return (
    <MarketingLayout>
      <article>
        <header className="border-b border-border">
          <div className="mx-auto max-w-3xl px-6 pt-16 pb-12">
            <nav className="text-[13px] text-muted-foreground flex items-center gap-2">
              <Link
                to="/support"
                className="hover:text-foreground transition inline-flex items-center gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> {C[0]}
              </Link>
              <span>·</span>
              <span>{categoryT}</span>
            </nav>
            <h1 className="mt-6 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.03em] text-foreground">
              {titleT}
            </h1>
            <p className="mt-5 text-[17px] leading-[1.55] text-muted-foreground max-w-2xl">
              {summaryT}
            </p>
            <div className="mt-6 flex items-center gap-5 text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {C[1]} {formatDate(article.updated)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {article.readMinutes} {C[2]}
              </span>
            </div>
          </div>
        </header>

        <section className="py-14">
          <div className="mx-auto max-w-3xl px-6">
            {bodyT.map((s: ArticleSection, i: number) => renderSection(s, i))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="border-t border-border bg-cream py-16">
            <div className="mx-auto max-w-3xl px-6">
              <div className="font-mono-eyebrow mb-6">
                {C[3]} {categoryT}
              </div>
              <ul className="divide-y divide-border">
                {related.map((r: SupportArticle, ri: number) => (
                  <li key={r.slug}>
                    <Link
                      to="/support/$slug"
                      params={{ slug: r.slug }}
                      className="group flex items-center justify-between gap-6 py-4"
                    >
                      <div>
                        <div className="text-[15px] font-medium text-foreground">
                          {relT[ri]?.title ?? r.title}
                        </div>
                        <div className="mt-1 text-[13px] text-muted-foreground line-clamp-1">
                          {relT[ri]?.summary ?? r.summary}
                        </div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <section className="border-t border-border py-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="font-serif text-[28px] leading-tight tracking-[-0.02em]">{C[4]}</h2>
            <p className="mt-3 text-[14.5px] text-muted-foreground">
              {C[5]}{" "}
              <a href="mailto:hello@realitytwin.io" className="underline text-foreground">
                {C[6]}
              </a>{" "}
              {C[7]}
            </p>
          </div>
        </section>
      </article>
    </MarketingLayout>
  );
}

function ArticleNotFound() {
  return (
    <MarketingLayout>
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <div className="font-mono-eyebrow">404 · Article not found</div>
        <h1 className="mt-3 font-serif text-[40px] leading-tight tracking-[-0.025em]">
          We couldn't find that support article.
        </h1>
        <p className="mt-4 text-[15px] text-muted-foreground">
          It may have moved or been retired. Head back to the{" "}
          <Link to="/support" className="underline text-foreground">
            Support Center
          </Link>{" "}
          to find what you need.
        </p>
        <div className="mt-10 text-left">
          <div className="font-mono-eyebrow mb-3">Popular articles</div>
          <ul className="divide-y divide-border">
            {SUPPORT_ARTICLES.slice(0, 6).map((a) => (
              <li key={a.slug}>
                <Link
                  to="/support/$slug"
                  params={{ slug: a.slug }}
                  className="block py-3 text-[14.5px] text-foreground hover:underline"
                >
                  {a.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </MarketingLayout>
  );
}

function ArticleError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <MarketingLayout>
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-serif text-[36px] leading-tight tracking-[-0.025em]">
          Something went wrong.
        </h1>
        <p className="mt-4 text-[14.5px] text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-8 inline-flex rounded-full bg-foreground px-5 py-2 text-[13.5px] text-background hover:opacity-90 transition"
        >
          Try again
        </button>
      </div>
    </MarketingLayout>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}
